/**
 * Why an SMS was not sent — the `sent_sms.skipReason` codes (status
 * `SKIPPED`, `ActionResult.smsSkipReason`) and the `smsSkipReason` locale key
 * each one renders as.
 *
 * MIRROR of main-consumer `src/lib/sent-sms-repo.ts` `SmsSkipReason` (the only
 * writer) and of the comment on main-backend
 * `org/nikita/app/sms_sent/SentSms.java` `skipReason`; drift-checked by
 * e2e_main `scripts/check-sms-reasons-drift.sh`. A code the consumer adds and
 * this map does not know still renders — as the generic `unknown` line
 * carrying the raw code.
 *
 * Static on purpose, like `emailSkipReason.ts`: every vitest mock of
 * next-intl in this repo returns a bare function, so `t.has()` cannot decide it.
 */
export const SMS_SKIP_REASON_KEYS = {
  /** The guest has neither `mobile` nor `phone`. */
  "no-phone": "noPhone",
  /** The stored number does not normalise to E.164. */
  "invalid-phone": "invalidPhone",
  /** The number's country is outside the allow-list (DE, AT, CH). */
  "country-unsupported": "countryUnsupported",
  /** `smsOptIn` is not `true` (strict consent), or the carrier reports the number opted out. */
  "sms-opt-out": "optOut",
  /** The event's company has no SMS allowance (`smsMonthlyCap` ≤ 0). */
  "sms-not-enabled": "notEnabled",
  /** Monthly allowance plus balance do not cover the message's parts. */
  "sms-budget-exhausted": "budgetExhausted",
  /** A daily brake: 3 attempts per number, or the workflow's daily parts. */
  "sms-rate-limited": "rateLimited",
  /** A fixed recipient id that no longer exists (number deleted) or belongs to another company. */
  "recipient-missing": "recipientMissing",
  /** A fixed recipient whose number is not verified (yet). */
  "recipient-unverified": "recipientUnverified",
  /** The guest document was gone by the time the send ran. */
  "guest-missing": "guestMissing",
  /** The consumer's channel switch is `off`. */
  "channel-off": "channelOff",
  /** The RENDERED body (placeholders filled) took more than 3 parts. */
  "body-too-long": "bodyTooLong",
  /**
   * The event's SMS plugin is off (SMS plugin wave). An `ActionResult` only —
   * never on a `sent_sms` row (the consumer skips before any row is written).
   */
  "plugin-off": "pluginOff",
  /**
   * The SEND_SMS picks an SMS template the event no longer has (SMS templates
   * wave). An `ActionResult` only, like `plugin-off` — nobody was read,
   * claimed or charged.
   */
  "template-missing": "templateMissing",
  /**
   * SMS manual send: a guest of a halted send, never tried — on the job's skip
   * list and counts only, never on a `sent_sms` row.
   */
  halted: "halted",
  /** SMS manual send: the dlq-consumer's skip entry for a dead SMS_MANUAL message, never on a row. */
  "dead-letter": "deadLetter",
  /**
   * SMS manual send: the guest left the active list (archived, declined, moved
   * off the list) after the submit — skipped when its message ran, on the
   * job's skip list and counts only, never on a row (audit r1 S-L4).
   */
  "not-on-list": "notOnList",
} as const;

export type SmsSkipReasonCode = keyof typeof SMS_SKIP_REASON_KEYS;
export type SmsSkipReasonKey = (typeof SMS_SKIP_REASON_KEYS)[SmsSkipReasonCode];

/** Every code this build knows, for tests and the drift gate. */
export const SMS_SKIP_REASON_CODES = Object.keys(SMS_SKIP_REASON_KEYS) as SmsSkipReasonCode[];

/** The locale key for a stored code, or null for one this build does not know. */
export function smsSkipReasonKey(code: string): SmsSkipReasonKey | null {
  return Object.prototype.hasOwnProperty.call(SMS_SKIP_REASON_KEYS, code)
    ? SMS_SKIP_REASON_KEYS[code as SmsSkipReasonCode]
    : null;
}
